/**
 * Zugriff auf die Lohnverrechnung (AP-38-D).
 *
 * Wer macht die Lohnverrechnung? Zwei Antworten auf einer Seite:
 *
 * - **Intern:** ein Schalter je Mitarbeiter. Er vergibt die Rolle
 *   „Lohnverrechnung" und, wo der Pool Plätze zählt, den PAYROLL-Platz —
 *   sofort gespeichert (AP-36 Fall 1), eine Speicherstelle je Zeile
 *   (`payrollAccess:<id>`). Kein freier Platz → die Meldung steht am Schalter.
 * - **Extern:** eine Person außerhalb des Betriebs, etwa die Steuerberatung.
 *   „Externe Person einladen" (Fall 2, Knopf „Einladen"), „Zugang entziehen" und
 *   „Erneut einladen" sind Handlungen (Fall 3).
 *
 * Die externe Person sieht nach der Annahme nur die Lohnverrechnung; ihre
 * E-Mail-Adresse ändert nur sie selbst (AP-38-C).
 */

import { useCallback, useEffect, useState } from 'react';
import { UserPlus } from 'lucide-react';
import { Button, Modal } from '@symphenia/ui-components';
import { bestaetige } from '@/lib/dialoge';
import { apiErrorText } from '@/lib/api-error';
import { fieldLabel, fieldWrapper, inputBase, pageWide } from '@/lib/form-styles';
import { Abschnitt, FeldStatus, FeldStatusZeichen, useFeld, useSpeicherstelle } from '@/lib/speichern';
import {
  fetchPayrollAccess,
  inviteExternalPayroll,
  reinviteExternalPayroll,
  revokeExternalPayroll,
  setPayrollAccess,
} from '../api/payroll.api';
import type { PayrollAccess, PayrollAccessExtern, PayrollAccessPerson } from '../types/payroll.types';
import { Hilfe } from '../components/Hilfe';

function nameVon(p: { firstName: string | null; lastName: string | null; email: string }): string {
  return [p.lastName, p.firstName].filter(Boolean).join(' ') || p.email;
}

const STATUS: Record<PayrollAccessExtern['status'], { text: string; klasse: string }> = {
  ACTIVE: { text: 'aktiv', klasse: 'bg-green-500/15 text-green-700 dark:text-green-400' },
  INVITED: { text: 'eingeladen', klasse: 'bg-amber-500/15 text-amber-700 dark:text-amber-400' },
  ENDED: { text: 'beendet', klasse: 'bg-muted text-muted-foreground' },
};

export default function PayrollAccessPage() {
  const [daten, setDaten] = useState<PayrollAccess | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [einladen, setEinladen] = useState(false);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  const laden = useCallback(async () => {
    try {
      setDaten(await fetchPayrollAccess());
      setFehler(null);
    } catch (e) {
      setFehler(apiErrorText(e, 'Der Zugriff konnte nicht geladen werden.'));
    }
  }, []);

  useEffect(() => {
    void laden();
  }, [laden]);

  const handle = async (id: string, aktion: () => Promise<unknown>) => {
    setLaeuft(id);
    setFehler(null);
    try {
      await aktion();
      await laden();
    } catch (e) {
      setFehler(apiErrorText(e, 'Das hat nicht geklappt.'));
    } finally {
      setLaeuft(null);
    }
  };

  const entziehen = async (p: PayrollAccessExtern) => {
    const ok = await bestaetige(
      p.status === 'INVITED'
        ? `Die Einladung an ${nameVon(p)} zurückziehen? Der Link in der E-Mail gilt danach nicht mehr.`
        : `${nameVon(p)} den Zugang zur Lohnverrechnung entziehen? Die Person kommt danach nicht mehr in diese Firma; was sie erfasst hat, bleibt.`,
      { ok: p.status === 'INVITED' ? 'Einladung zurückziehen' : 'Zugang entziehen', gefaehrlich: true },
    );
    if (ok) await handle(p.id, () => revokeExternalPayroll(p.id));
  };

  const plaetze = daten?.plaetze;

  return (
    <div className={pageWide}>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Zugriff</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Wer die Lohnverrechnung macht — im Betrieb oder extern, etwa die Steuerberatung.
          </p>
        </div>
        <Hilfe titel="Zugriff">
          <p>
            Der Schalter <strong>Lohnverrechnung</strong> gibt einem Mitarbeiter die Rolle „Lohnverrechnung": alle
            Seiten dieses Moduls, aber nicht diese Seite — wer die Lohnverrechnung macht, vergibt sie nicht weiter.
          </p>
          <p>
            Eine <strong>externe Person</strong> bekommt eine Einladung per E-Mail. Arbeitet sie schon für eine andere
            Firma in Symphenia, meldet sie sich mit ihrem Konto an und wählt die Firma oben in der Leiste. Sie sieht nur
            die Lohnverrechnung und steht in keiner Mitarbeiterliste.
          </p>
          <p>Jeder Zugang zur Lohnverrechnung belegt einen Platz, auch der externe.</p>
        </Hilfe>
      </div>

      {fehler && <div className="mb-4 rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}

      {plaetze?.zaehlt && (
        <p className="mb-4 text-sm text-muted-foreground" data-testid="plaetze">
          Plätze: {plaetze.belegt} von {plaetze.gesamt} belegt
        </p>
      )}

      {daten && (
        <div className="space-y-6">
          <Abschnitt titel="Im Betrieb">
            {daten.mitarbeiter.length === 0 ? (
              <p className="text-sm text-muted-foreground">Keine aktiven Mitarbeiter.</p>
            ) : (
              <ul className="divide-y divide-border">
                {daten.mitarbeiter.map((m) => (
                  <MitarbeiterZeile key={m.id} person={m} onGeaendert={laden} />
                ))}
              </ul>
            )}
          </Abschnitt>

          <Abschnitt
            titel="Extern"
            aktionen={
              <Button size="sm" onClick={() => setEinladen(true)}>
                <UserPlus size={14} className="mr-1.5" />
                Externe Person einladen
              </Button>
            }
          >
            {daten.externe.length === 0 ? (
              <p className="text-sm text-muted-foreground">Noch niemand von außerhalb.</p>
            ) : (
              <ul className="divide-y divide-border">
                {daten.externe.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center gap-3 py-2 text-sm">
                    <span className="min-w-0 flex-1">
                      <span className="font-medium text-foreground">{nameVon(p)}</span>
                      <span className="ml-2 text-muted-foreground">{p.email}</span>
                    </span>
                    <span className={`rounded-full px-2 py-0.5 text-xs ${STATUS[p.status].klasse}`}>
                      {STATUS[p.status].text}
                    </span>
                    {p.status === 'ENDED' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={laeuft === p.id}
                        onClick={() => void handle(p.id, () => reinviteExternalPayroll(p.id))}
                      >
                        Erneut einladen
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" disabled={laeuft === p.id} onClick={() => void entziehen(p)}>
                        {p.status === 'INVITED' ? 'Einladung zurückziehen' : 'Zugang entziehen'}
                      </Button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Abschnitt>
        </div>
      )}

      <EinladenDialog
        isOpen={einladen}
        onClose={() => setEinladen(false)}
        onEingeladen={() => {
          setEinladen(false);
          void laden();
        }}
        onListeNeu={() => void laden()}
      />
    </div>
  );
}

/** Eine Zeile „Im Betrieb" — eine Speicherstelle je Mitarbeiter, der Schalter speichert sofort. */
function MitarbeiterZeile({ person, onGeaendert }: { person: PayrollAccessPerson; onGeaendert: () => void }) {
  const stelle = useSpeicherstelle<unknown>({
    schluessel: `payrollAccess:${person.id}`,
    bezeichnung: `Lohnverrechnung ${nameVon(person)}`,
    daten: person,
    schreibe: async (patch) => {
      const zeile = await setPayrollAccess(person.id, Boolean(patch.lohnverrechnung));
      // Platzzahl und Hinweise der ganzen Seite folgen dem Schalter.
      onGeaendert();
      return zeile ?? { ...person, ...patch };
    },
    keepalive: { url: `/payroll/access/${person.id}`, method: 'PUT' },
  });
  const f = useFeld<boolean>(stelle, 'lohnverrechnung', { art: 'schalter', label: 'Lohnverrechnung' });

  return (
    <li className="py-2 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <span className="min-w-0 flex-1">
          <span className="font-medium text-foreground">{nameVon(person)}</span>
          <span className="ml-2 text-muted-foreground">{person.email}</span>
        </span>
        {person.ueberAndereRolle && (
          <span
            className="text-xs text-muted-foreground"
            title="Diese Person hat die Lohnverrechnung über eine andere Rolle, etwa als Administrator. Der Schalter nimmt sie ihr nicht."
          >
            über eine andere Rolle
          </span>
        )}
        <label className="flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            aria-label={`Lohnverrechnung für ${nameVon(person)}`}
            className="h-4 w-4 accent-[hsl(var(--primary))]"
            {...f.schalter}
          />
          <span>Lohnverrechnung</span>
          <FeldStatusZeichen status={f} />
        </label>
      </div>
      <FeldStatus status={f} zeichen={false} />
    </li>
  );
}

/** „Externe Person einladen" — nur, was zum Einladen nötig ist (AP-36 Fall 2). */
function EinladenDialog({
  isOpen,
  onClose,
  onEingeladen,
  onListeNeu,
}: {
  isOpen: boolean;
  onClose: () => void;
  onEingeladen: () => void;
  /** Auch nach einem Fehler: Ist die Mail gescheitert, steht die Person trotzdem schon in der Liste. */
  onListeNeu: () => void;
}) {
  const [email, setEmail] = useState('');
  const [vorname, setVorname] = useState('');
  const [nachname, setNachname] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setEmail('');
    setVorname('');
    setNachname('');
    setFehler(null);
  }, [isOpen]);

  const senden = async () => {
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) return setFehler('Bitte eine gültige E-Mail-Adresse angeben.');
    setSendet(true);
    setFehler(null);
    try {
      await inviteExternalPayroll({
        email: email.trim(),
        firstName: vorname.trim() || undefined,
        lastName: nachname.trim() || undefined,
      });
      onEingeladen();
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Einladung konnte nicht gesendet werden.'));
      onListeNeu();
    } finally {
      setSendet(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Externe Person einladen"
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={sendet}>
            Abbrechen
          </Button>
          <Button onClick={() => void senden()} disabled={sendet}>
            {sendet ? 'Einladen…' : 'Einladen'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {fehler && <div className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">{fehler}</div>}
        <p className="text-sm text-muted-foreground">
          Die Person bekommt einen Link per E-Mail. Nach der Annahme sieht sie in dieser Firma nur die Lohnverrechnung.
        </p>
        <div className={fieldWrapper}>
          <label className={fieldLabel} htmlFor="pa-email">
            E-Mail
          </label>
          <input
            id="pa-email"
            type="email"
            className={inputBase}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className={fieldWrapper}>
            <label className={fieldLabel} htmlFor="pa-vorname">
              Vorname
            </label>
            <input id="pa-vorname" className={inputBase} value={vorname} onChange={(e) => setVorname(e.target.value)} />
          </div>
          <div className={fieldWrapper}>
            <label className={fieldLabel} htmlFor="pa-nachname">
              Nachname
            </label>
            <input
              id="pa-nachname"
              className={inputBase}
              value={nachname}
              onChange={(e) => setNachname(e.target.value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
}
